import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateLocation } from '@/hooks/use-locations';
import { useT } from '@/i18n/use-t';

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

export function LocationNewPage() {
  const t = useT('admin_ui');
  const navigate = useNavigate();
  const create = useCreateLocation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [timezone, setTimezone] = useState('');
  const [banner, setBanner] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBanner(null);
    try {
      const created = await create.mutateAsync({
        code: code.trim(),
        name: name.trim(),
        addressLine: addressLine.trim() === '' ? null : addressLine.trim(),
        city: city.trim() === '' ? null : city.trim(),
        region: region.trim() === '' ? null : region.trim(),
        postalCode: postalCode.trim() === '' ? null : postalCode.trim(),
        countryCode: countryCode.trim() === '' ? null : countryCode.trim().toUpperCase(),
        timezone: timezone.trim() === '' ? null : timezone.trim(),
      });
      navigate(`/locations/${created.id}`);
    } catch (err) {
      setBanner(extractError(err) ?? t('locations.new.createFailed', 'Create failed.'));
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <Link to="/locations" className="text-xs text-muted-foreground hover:underline">
          ← {t('locations.list.title', 'Locations')}
        </Link>
        <h1 className="text-2xl font-semibold">{t('locations.new.title', 'New location')}</h1>
      </header>

      <form
        onSubmit={onSubmit}
        className="grid grid-cols-2 gap-4 rounded-md border border-border bg-card p-4"
      >
        <div>
          <Label htmlFor="code">{t('locations.field.code', 'Code')}</Label>
          <Input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="STORE-01"
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="name">{t('locations.field.name', 'Name')}</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Demo Store 01"
            required
            className="mt-1"
          />
        </div>

        <div className="col-span-2">
          <Label htmlFor="addressLine">{t('locations.field.address', 'Address')}</Label>
          <Input
            id="addressLine"
            value={addressLine}
            onChange={(e) => setAddressLine(e.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="city">{t('locations.field.city', 'City')}</Label>
          <Input
            id="city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="region">{t('locations.field.region', 'Region')}</Label>
          <Input
            id="region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="postalCode">{t('locations.field.postalCode', 'Postal code')}</Label>
          <Input
            id="postalCode"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="countryCode">{t('locations.field.countryIso', 'Country (ISO-2)')}</Label>
          <Input
            id="countryCode"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            placeholder="LT"
            maxLength={2}
            className="mt-1"
          />
        </div>

        <div className="col-span-2">
          <Label htmlFor="timezone">{t('locations.field.timezone', 'Timezone')}</Label>
          <Input
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="Europe/Vilnius"
            className="mt-1"
          />
        </div>

        {banner !== null && (
          <p className="col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {banner}
          </p>
        )}

        <div className="col-span-2 flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending
              ? t('locations.new.creating', 'Creating…')
              : t('locations.new.submit', 'Create location')}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/locations">{t('common.cancel', 'Cancel')}</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
